import { useCallback } from "react";
import { CircleCheck, RefreshCw, X } from "lucide-react";
import { useQueryClient } from "react-query";
import { toast } from "react-toastify";

import CustomButton from "@/components/desktop/button/CustomButton";
import CustomBadge from "@/components/desktop/badge/CustomBadge";
import CustomModalForm from "@/components/desktop/form/modal/CustomModalForm/CustomModalForm";
import Fetching from "@/components/Fetching/Fetching";

import { oaKey, useConsumeOAProductLine, useOAConsumptionAvailability } from "@/hooks/planning/oa/useOA";
import { formatUnitMeasure } from "@/services/Utils";

import { getApplicationOrderStateVariant } from "@/constants/applicationOrderStates";

import styles from "./modal-oa-product-consumption.module.scss";

const quantityNumberFormatter = new Intl.NumberFormat("es-CL", {
	minimumFractionDigits: 0,
	maximumFractionDigits: 2,
});

const normalizeQuantityValue = (quantity) => {
	if (typeof quantity === "number") {
		return Number.isFinite(quantity) ? quantity : 0;
	}
	if (typeof quantity === "string") {
		const normalizedText = quantity.includes(",") ? quantity.replace(/\./g, "").replace(",", ".") : quantity;
		const parsedQuantity = Number(normalizedText);
		return Number.isFinite(parsedQuantity) ? parsedQuantity : 0;
	}
	return 0;
};

const formatQuantity = (quantity, unitMeasurement) => {
	const normalizedQuantity = normalizeQuantityValue(quantity);
	return `${quantityNumberFormatter.format(normalizedQuantity)}${formatUnitMeasure(unitMeasurement)}`;
};

const ModalOAProductConsumption = ({ code, onClose }) => {
	const queryClient = useQueryClient();
	const { data, isLoading, isFetching, refetch } = useOAConsumptionAvailability(code);
	const { mutate: consumeProductLine, isLoading: isConsuming } = useConsumeOAProductLine();

	const handleConsumeLine = useCallback(
		(line) => {
			consumeProductLine(
				{ oa_id: code, detail_id: line.detail_id },
				{
					onSuccess: (response) => {
						toast.success("Producto consumido correctamente.");
						queryClient.setQueryData(["oa-consumption-availability", code], response);
						queryClient.invalidateQueries(oaKey);
					},
					onError: (mutationError) => {
						const serverMessage = mutationError?.response?.data?.errors?.[0];
						toast.error(serverMessage || "No se pudo consumir el producto.");
					},
				},
			);
		},
		[code, consumeProductLine, queryClient],
	);

	if (!code) {
		return null;
	}

	const lines = data?.lines || [];
	const isProcessing = isLoading || isConsuming;
	const stateVariant = getApplicationOrderStateVariant(Number(data?.state_id));

	return (
		<CustomModalForm
			open={Boolean(code)}
			onClose={onClose}
			title={`Consumir productos OA ${data?.code || ""}`}
			size="xxl"
			closeOnBackdrop={false}
			closeOnEsc={false}
			showSave={false}
			footer={
				<>
					<CustomButton variant="secondary" startIcon={<RefreshCw size={16} />} onClick={() => refetch()} loading={isFetching && !isLoading}>
						Actualizar
					</CustomButton>
					<CustomButton variant="secondary" startIcon={<X size={16} />} onClick={onClose} disabled={isConsuming}>
						Cerrar
					</CustomButton>
				</>
			}
		>
			{isLoading ? (
				<Fetching newStyles={{ marginTop: "2em", marginBottom: "2em" }} />
			) : (
				<div className={styles.content}>
					{isConsuming && (
						<div className={styles.loadingOverlay}>
							<Fetching topMargin="0" bottomMargin="0" message="Consumiendo producto..." />
						</div>
					)}
					<div className={styles.summaryBar}>
						<div className={styles.summaryItem}>
							<span>Estado</span>
							<CustomBadge variant={stateVariant} size="sm">
								{data?.state_name || "Sin información"}
							</CustomBadge>
						</div>
						<div className={styles.summaryItem}>
							<span>Bodega OA</span>
							<strong>{data?.estate_name || "Sin información"}</strong>
						</div>
					</div>

					{lines.length === 0 ? (
						<div className={styles.emptyState}>No hay productos planificados para consumir.</div>
					) : (
						<div className={styles.lines}>
							{lines.map((line) => {
								const isLineFinished = normalizeQuantityValue(line.pending_quantity) <= 0;
								const canShowConsumeButton = line.can_consume && !isLineFinished;

								return (
									<div key={line.detail_id} className={`${styles.lineRow} ${isLineFinished ? styles.lineFinished : ""}`}>
										<div className={styles.productBlock}>
											<strong>{line.product_name}</strong>
											<span>{formatQuantity(line.planned_quantity, line.unit_measurement)} planificado</span>
										</div>

										<div className={styles.quantityGrid}>
											<div>
												<span>Pendiente</span>
												<strong>{formatQuantity(line.pending_quantity, line.unit_measurement)}</strong>
											</div>
											<div>
												<span>Disponible</span>
												<strong>{formatQuantity(line.available_quantity, line.unit_measurement)}</strong>
											</div>
										</div>

										<div className={styles.codesBlock}>
											{canShowConsumeButton && line.allocations.length > 0
												? line.allocations.map((allocation) => (
														<span key={`${line.detail_id}-${allocation.inventory_id}`} className={styles.codePill}>
															{allocation.code} ({formatQuantity(allocation.quantity, line.unit_measurement)})
														</span>
													))
												: null}
										</div>

										<div className={styles.actionBlock}>
											{isLineFinished ? (
												<span className={styles.finishedCheck}>
													<CircleCheck size={15} />
													Producto consumido
												</span>
											) : canShowConsumeButton ? (
												<CustomButton variant="primary" onClick={() => handleConsumeLine(line)} disabled={isProcessing} loading={false}>
													Consumir producto
												</CustomButton>
											) : null}
											<span
												className={
													isLineFinished
														? styles.finishedMessage
														: line.can_consume
															? styles.availableMessage
															: styles.unavailableMessage
												}
											>
												{isLineFinished ? "La línea no tiene productos pendientes." : line.message}
											</span>
										</div>
									</div>
								);
							})}
						</div>
					)}
				</div>
			)}
		</CustomModalForm>
	);
};

export default ModalOAProductConsumption;
